import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ChooseDeliveryCarrierPanel from './ChooseDeliveryCarrierPanel';
import { useDeliveryPricing } from '../hooks/useDeliveryPricing';
import apiService from '../services/api';
import type { FoundObject } from '../types/foundObject';
import {
  formatAmountEur,
  getAvailableDeliveryCarriers,
  getDeliveryCarrierDefaultAmountCents,
  type DeliveryCarrier,
} from '../utils/deliveryPricing';
import {
  buildDeliveryOfferMessage,
  centsToEuroInput,
  euroInputToCents,
  MAX_OFFER_AMOUNT_CENTS,
  MIN_OFFER_AMOUNT_CENTS,
} from '../utils/deliveryOfferMessage';

type Props = {
  open: boolean;
  object: FoundObject | null;
  locationProviderId?: number;
  onClose: () => void;
  onSuccess: (updated?: FoundObject) => void;
};

export default function AccountlessRestitutionDialog({
  open,
  object,
  locationProviderId,
  onClose,
  onSuccess,
}: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [carrier, setCarrier] = useState<DeliveryCarrier | null>(null);
  const [amountEur, setAmountEur] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: deliveryPricing } = useDeliveryPricing({
    locationProviderId,
    foundObjectId: object?.id,
  });

  const carriers = useMemo(
    () => getAvailableDeliveryCarriers(deliveryPricing),
    [deliveryPricing]
  );

  useEffect(() => {
    if (!open || !object) return;
    const name = (object.claimantName ?? object.claimant_name ?? '').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    setFirstName(parts[0] ?? '');
    setLastName(parts.slice(1).join(' '));
    setEmail((object.claimantEmail ?? object.claimant_email ?? '').trim());
    setSubmitError(null);
    setAmountError(null);
    const initial = carriers.length === 1 ? carriers[0] : null;
    setCarrier(initial);
    setAmountEur(
      centsToEuroInput(
        getDeliveryCarrierDefaultAmountCents(initial ?? carriers[0], deliveryPricing, {
          categoryId: undefined,
          itemType: deliveryPricing?.resolved_context?.item_type,
          weightKg: deliveryPricing?.resolved_context?.weight_kg,
        })
      )
    );
  }, [open, object, carriers, deliveryPricing]);

  const handleCarrierChange = (next: DeliveryCarrier) => {
    setCarrier(next);
    setAmountEur(
      centsToEuroInput(
        getDeliveryCarrierDefaultAmountCents(next, deliveryPricing, {
          itemType: deliveryPricing?.resolved_context?.item_type,
          weightKg: deliveryPricing?.resolved_context?.weight_kg,
        })
      )
    );
    setAmountError(null);
  };

  const handleSubmit = async () => {
    if (!object || !carrier) return;
    const cents = euroInputToCents(amountEur);
    if (cents == null) {
      setAmountError(
        `Tarif invalide (entre ${formatAmountEur(MIN_OFFER_AMOUNT_CENTS / 100)} et ${formatAmountEur(MAX_OFFER_AMOUNT_CENTS / 100)}).`
      );
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError('Le prénom et le nom sont requis.');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setSubmitError('Adresse e-mail invalide.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Construit le texte d'offre pour cohérence (le backend reconstruit aussi les tags).
      buildDeliveryOfferMessage(carrier, undefined, cents);
      const res = await apiService.sendAccountlessDeliveryOffer(object.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        carrier,
        amount_cents: cents,
      });
      onSuccess(res.data.found_object);
      onClose();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } };
      setSubmitError(apiErr?.response?.data?.error || 'Envoi impossible. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Restitution sans compte</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Saisissez les coordonnées de la personne identifiée comme propriétaire. Elle recevra par
          e-mail une proposition de livraison (sans créer de compte), pourra payer via Stripe, puis
          vous pourrez imprimer le bon d&apos;expédition.
        </Typography>
        {object ? (
          <Typography variant="subtitle2" fontWeight={700}>
            {object.title}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
            sx={{ flex: '1 1 180px' }}
            required
          />
          <TextField
            label="Nom"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={submitting}
            sx={{ flex: '1 1 180px' }}
            required
          />
        </Box>
        <TextField
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          required
          fullWidth
        />
        <ChooseDeliveryCarrierPanel
          value={carrier}
          onChange={handleCarrierChange}
          pricing={deliveryPricing}
          locationProviderId={locationProviderId}
          disabled={submitting}
        />
        <TextField
          label="Tarif proposé (€)"
          value={amountEur}
          onChange={(e) => {
            setAmountEur(e.target.value);
            setAmountError(null);
          }}
          error={!!amountError}
          helperText={
            amountError
            || 'Prérempli avec le tarif admin. Modifiez-le pour un objet plus lourd, fragile, etc.'
          }
          disabled={submitting}
          inputProps={{ inputMode: 'decimal', min: 1, step: 0.5 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          sx={{ maxWidth: 220 }}
        />
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || !carrier || carriers.length === 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Envoyer la proposition
        </Button>
      </DialogActions>
    </Dialog>
  );
}
